import { directSupabase } from '../../lib/supabaseClient';
import {
  commentAuthorName,
  seededNoteTimestamp,
  type ConversationComment,
} from './workspaceComments.model';

export const WORKSPACE_COMMENTS_MISSING =
  'The workspace comments table is not in the database yet. Apply migration 20261006120000-workspace-comments.sql.';

function isMissingTable(message?: string | null) {
  return /project_workspace_comments/i.test(String(message || ''))
    && /does not exist|schema cache|not find/i.test(String(message || ''));
}

function table() {
  return (directSupabase as any).from('project_workspace_comments');
}

async function profileNames(ids: string[]): Promise<Map<string, { name: string; role: string | null }>> {
  const unique = [...new Set(ids.filter(Boolean))];
  const names = new Map<string, { name: string; role: string | null }>();
  if (!unique.length) return names;
  const { data, error } = await directSupabase
    .from('profiles')
    .select('id, full_name, email, role')
    .in('id', unique);
  if (error) throw new Error(error.message);
  (data || []).forEach((row: { id: string; full_name?: string | null; email?: string | null; role?: string | null }) => {
    names.set(row.id, {
      name: commentAuthorName(row.full_name, row.email),
      role: row.role || null,
    });
  });
  return names;
}

function asWorkspaceComment(
  row: { id: string; author_id: string; body: string; created_at: string; updated_at?: string | null },
  names: Map<string, { name: string; role: string | null }>,
): ConversationComment {
  const person = names.get(row.author_id);
  return {
    id: row.id,
    source: 'workspace',
    body: row.body,
    createdAt: row.created_at,
    updatedAt: row.updated_at || null,
    authorId: row.author_id,
    authorName: person?.name || 'Staff',
    authorRole: person?.role || null,
  };
}

type WorkspaceCommentRow = {
  id: string;
  author_id: string;
  body: string;
  created_at: string;
  updated_at?: string | null;
};

async function adminAuthorId(): Promise<string | null> {
  const { data, error } = await directSupabase
    .from('profiles')
    .select('id, full_name, email, role, is_active')
    .eq('role', 'admin');
  if (error) throw new Error(error.message);
  const rows = ((data || []) as Array<{
    id: string;
    full_name?: string | null;
    email?: string | null;
    is_active?: boolean | null;
  }>).filter((row) => row.is_active !== false);
  const pool = rows.length ? rows : ((data || []) as typeof rows);
  const named = pool.find((row) => String(row.full_name || '').trim().toLowerCase() === 'admin');
  if (named) return named.id;
  const mailbox = pool.find((row) => String(row.email || '').trim().toLowerCase().startsWith('admin@'));
  return mailbox?.id || pool[0]?.id || null;
}

const notePromoteInFlight = new Map<string, Promise<boolean>>();

/** Copies a legacy workspace note into the conversation as the first Admin comment. */
export async function promoteLegacyWorkspaceNote(project: {
  id: string;
  notes?: string | null;
  created_at?: string | null;
}): Promise<boolean> {
  const body = String(project.notes || '').trim();
  if (!body) return false;
  const existing = notePromoteInFlight.get(project.id);
  if (existing) return existing;
  const run = promoteLegacyWorkspaceNoteOnce(project.id, body, project.created_at)
    .finally(() => { notePromoteInFlight.delete(project.id); });
  notePromoteInFlight.set(project.id, run);
  return run;
}

async function promoteLegacyWorkspaceNoteOnce(
  projectId: string,
  body: string,
  projectCreatedAt?: string | null,
): Promise<boolean> {
  const { data, error } = await table()
    .select('id, body, created_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });
  if (error) {
    if (isMissingTable(error.message)) return false;
    throw new Error(error.message);
  }
  const rows = (data || []) as Array<{ id: string; body: string; created_at: string }>;
  if (rows.some((row) => String(row.body || '').trim() === body)) return true;
  const authorId = await adminAuthorId();
  if (!authorId) throw new Error('No Admin profile is available to keep the existing note.');
  const createdAt = seededNoteTimestamp(projectCreatedAt, rows.map((row) => row.created_at));
  const { error: insertError } = await table().insert({
    project_id: projectId,
    author_id: authorId,
    body,
    created_at: createdAt,
    updated_at: createdAt,
  });
  if (insertError) {
    if (isMissingTable(insertError.message)) return false;
    throw new Error(insertError.message);
  }
  return true;
}

export async function loadWorkspaceConversation(projectId: string): Promise<{
  comments: ConversationComment[];
  tableMissing: boolean;
}> {
  const { data, error } = await table()
    .select('id, author_id, body, created_at, updated_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });
  if (error) {
    if (isMissingTable(error.message)) return { comments: [], tableMissing: true };
    throw new Error(error.message);
  }
  const workspaceRows = (data || []) as WorkspaceCommentRow[];
  const names = await profileNames(workspaceRows.map((row) => row.author_id));
  return {
    comments: workspaceRows.map((row) => asWorkspaceComment(row, names)),
    tableMissing: false,
  };
}

/** Copies a workspace's conversation onto another workspace, keeping authors and timestamps. */
export async function copyWorkspaceComments(sourceProjectId: string, targetProjectId: string): Promise<number> {
  const { data, error } = await table()
    .select('author_id, body, created_at, updated_at')
    .eq('project_id', sourceProjectId)
    .order('created_at', { ascending: true });
  if (error) {
    if (isMissingTable(error.message)) return 0;
    throw new Error(error.message);
  }
  const rows = (data || []) as Array<Omit<WorkspaceCommentRow, 'id'>>;
  if (!rows.length) return 0;
  const { error: insertError } = await table().insert(rows.map((row) => ({
    project_id: targetProjectId,
    author_id: row.author_id,
    body: row.body,
    created_at: row.created_at,
    updated_at: row.updated_at || row.created_at,
  })));
  if (insertError) throw new Error(insertError.message);
  return rows.length;
}

export async function addWorkspaceComment(projectId: string, authorId: string, body: string): Promise<void> {
  const trimmed = body.trim();
  if (!trimmed) return;
  const { error } = await table().insert({
    project_id: projectId,
    author_id: authorId,
    body: trimmed,
  });
  if (error) {
    if (isMissingTable(error.message)) throw new Error(WORKSPACE_COMMENTS_MISSING);
    throw new Error(error.message);
  }
}

export async function updateWorkspaceComment(commentId: string, body: string): Promise<void> {
  const trimmed = body.trim();
  if (!trimmed) throw new Error('Comment cannot be empty.');
  const { error } = await table().update({ body: trimmed }).eq('id', commentId);
  if (error) throw new Error(error.message);
}

export async function deleteWorkspaceComment(commentId: string): Promise<void> {
  const { error } = await table().delete().eq('id', commentId);
  if (error) throw new Error(error.message);
}
